/**
 * Card for help-request posts. Modelled on PostCard but with:
 * - Left accent border (orange/red)
 * - Status badges (Help Needed / Urgent / Resolved)
 * - "I can help" CTA → DM
 * - "Mark as resolved" for the author
 */
import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { LifeBuoy, MessageCircle, Flag, CheckCircle, Loader2, MoreHorizontal, Trash2, Pencil, Link2, Users, Globe } from 'lucide-react'
import type { HelpPost, FeedItem } from '@olimhub/shared'
import { Avatar } from '@/components/profile/Avatar'
import { LanguageBadge } from '@/components/common/LanguageBadge'
import { renderContent } from '@/lib/content-renderer'
import { formatPostTime } from '@/lib/dates'
import { accountPath } from '@/lib/accountPath'
import { useAuthStore } from '@/store/auth.store'
import { postsApi } from '@/api'
import { useToast } from '@/components/common/Toast'
import { usePostForm } from '@/context/PostFormContext'
import { PostBadge } from './PostBadge'
import { CategoryChip } from './CategoryChip'
import { ShareButton } from './ShareButton'
import { ReportModal } from '@/components/common/ReportModal'
import { feedScroll } from '@/lib/feedScroll'

interface InfiniteData {
  pages: Array<{ items: FeedItem[]; nextCursor: string | null }>
  pageParams: unknown[]
}

export interface HelpCardProps {
  post: HelpPost
  readOnly?: boolean
  onReport?: (id: string) => void
  hideGroupAttribution?: boolean
  canManage?: boolean
}

export function HelpCard({ post, readOnly = false, onReport, hideGroupAttribution = false, canManage = false }: HelpCardProps) {
  const { t } = useTranslation()
  const { account: authAccount, isAuthenticated } = useAuthStore()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { openEdit } = usePostForm()

  const groupAttribution = post.account.accountType === 'group'
    ? { name: post.account.displayName, path: accountPath(post.account) }
    : post.groupTag
      ? { name: post.groupTag.name, path: `/groups/${post.groupTag.slug}` }
      : null

  const help = post.help
  const [isResolved, setIsResolved] = useState(help.isResolved)
  const [isUrgent] = useState(help.isUrgent)
  const [confirmResolve, setConfirmResolve] = useState(false)
  const [isResolving, setIsResolving] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [repliesOpen, setRepliesOpen] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const isOwn = !!authAccount && authAccount.id === post.account.id
  const canManagePost = isOwn || canManage

  const accentColor = isUrgent && !isResolved ? '#DC2626' : '#E85D3A'

  const html = renderContent(post.content)
  const dir: 'ltr' | 'rtl' = post.language === 'he' ? 'rtl' : 'ltr'

  const loginRedirect = () => {
    navigate(`/login?from=${encodeURIComponent(location.pathname)}`)
  }

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('a, button, textarea, input, select, [role="button"]')) return
    feedScroll.save(document.getElementById('feed-scroll')?.scrollTop ?? 0)
    navigate(`/posts/${post.id}`)
  }

  const handleMarkResolved = async () => {
    setIsResolving(true)
    try {
      await postsApi.markResolved(post.id)
      setIsResolved(true)
      setConfirmResolve(false)
      queryClient.setQueriesData<InfiniteData>({ queryKey: ['feed'] }, (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((item) => {
              if (item.kind === 'post' && (item.data as HelpPost).id === post.id) {
                return {
                  ...item,
                  data: {
                    ...(item.data as HelpPost),
                    help: { ...(item.data as HelpPost).help, isResolved: true },
                  },
                }
              }
              return item
            }),
          })),
        }
      })
      toast.success(t('help.resolved'))
    } catch {
      toast.error('Could not mark as resolved.')
    } finally {
      setIsResolving(false)
    }
  }

  const copyLink = () => {
    void navigator.clipboard.writeText(window.location.origin + '/posts/' + post.id)
    toast.success('Link copied!')
  }

  const handleEdit = () => {
    setShowMenu(false)
    openEdit(post)
  }

  const handleDeleteConfirm = async () => {
    setIsDeleting(true)
    try {
      await postsApi.delete(post.id)
      toast.success('Post deleted')
      const removeFromCache = (old: InfiniteData | undefined) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter(
              (item) => !(item.kind === 'post' && (item.data as HelpPost).id === post.id),
            ),
          })),
        }
      }
      queryClient.setQueriesData<InfiniteData>({ queryKey: ['feed'] }, removeFromCache)
      queryClient.setQueriesData<InfiniteData>({ queryKey: ['group-feed'] }, removeFromCache)
    } catch {
      toast.error('Could not delete post.')
    } finally {
      setIsDeleting(false)
      setConfirmDelete(false)
      setShowMenu(false)
    }
  }

  const handleCanHelp = () => {
    if (!isAuthenticated) { loginRedirect(); return }
    navigate(
      `/messages/${post.account.username}?ref=${post.id}&type=help${post.category ? `&category=${encodeURIComponent(post.category.name)}` : ''}`,
    )
  }

  return (
    <div
      onClick={handleCardClick}
      style={{
        position: 'relative',
        background: 'var(--surface)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        isolation: 'isolate',
        cursor: 'pointer',
        zIndex: showMenu ? 1 : undefined,
        borderInlineStartWidth: 3,
        borderInlineStartStyle: 'solid',
        borderInlineStartColor: accentColor,
        opacity: isResolved ? 0.85 : 1,
      }}
    >
      {groupAttribution && !hideGroupAttribution && (
        <div
          onClick={(e) => { e.stopPropagation(); navigate(groupAttribution.path) }}
          style={{
            background: 'var(--surface-2)',
            borderRadius: 'calc(var(--radius-md) - 1px) calc(var(--radius-md) - 1px) 0 0',
            padding: '6px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            cursor: 'pointer',
          }}
        >
          <Users size={13} color="var(--brand)" style={{ flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--text-muted)' }}>
            {t('groups.posted_in', { name: groupAttribution.name })}
          </span>
        </div>
      )}

      {/* LifeBuoy watermark */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          borderRadius: 'var(--radius-md)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            bottom: -14,
            insetInlineEnd: -10,
            opacity: 0.06,
            color: 'var(--text-strong)',
          }}
        >
          <LifeBuoy size={108} />
        </div>
      </div>

      {/* Content wrapper */}
      <div style={{ position: 'relative', zIndex: 1, paddingBottom: readOnly ? 16 : 0 }}>
        {/* Header */}
        <div style={{ padding: '14px 16px 0', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <button
            type="button"
            onClick={() => navigate(accountPath(post.account))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0, display: 'flex' }}
          >
            <Avatar
              name={post.account.displayName || post.account.username}
              avatarUrl={post.account.avatarUrl}
              animalAvatar={post.account.animalAvatar}
              accountType={post.account.accountType}
              size="md"
            />
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Row 1: displayName + badges */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                onClick={() => navigate(accountPath(post.account))}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 700,
                  color: 'var(--text-strong)',
                  fontSize: 'var(--fs-body)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'start',
                }}
              >
                {post.account.displayName || post.account.username}
              </button>
              <div style={{ marginInlineStart: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                <PostBadge badges={post.account.badges} />
                {hideGroupAttribution && post.showInLocalFeed && (
                  <span title={t('groups.also_in_local')} style={{ display: 'flex', color: 'var(--text-faint)' }}>
                    <Globe size={14} />
                  </span>
                )}
                <LanguageBadge lang={post.language} size="sm" />
                {/* Status badge */}
                {isResolved ? (
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-pill)',
                      background: 'color-mix(in srgb, #16A34A 15%, transparent)',
                      color: '#16A34A',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 700,
                      border: '1px solid #16A34A',
                      flexShrink: 0,
                    }}
                  >
                    ✓ {t('help.resolved')}
                  </span>
                ) : isUrgent ? (
                  <span
                    className="help-urgent-pulse"
                    style={{
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-pill)',
                      background: '#DC2626',
                      color: '#fff',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 700,
                      border: '1px solid #DC2626',
                      flexShrink: 0,
                    }}
                  >
                    {t('help.urgent')}
                  </span>
                ) : (
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-pill)',
                      background: 'color-mix(in srgb, #E85D3A 15%, transparent)',
                      color: '#E85D3A',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-xs)',
                      fontWeight: 700,
                      border: '1px solid #E85D3A',
                      flexShrink: 0,
                    }}
                  >
                    {t('help.needed')}
                  </span>
                )}

                {canManagePost && !readOnly && (
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      aria-label="Post options"
                      onClick={() => { setShowMenu((v) => !v); setConfirmDelete(false) }}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-muted)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 4,
                        borderRadius: 'var(--radius-sm)',
                      }}
                    >
                      <MoreHorizontal size={16} />
                    </button>

                    {showMenu && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          insetInlineEnd: 0,
                          zIndex: 50,
                          background: 'var(--surface)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-md)',
                          boxShadow: 'var(--shadow-modal)',
                          minWidth: 140,
                          overflow: 'hidden',
                        }}
                      >
                        <button
                          type="button"
                          onClick={handleEdit}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 14px',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-sans)',
                            fontSize: 'var(--fs-sm)',
                            color: 'var(--text-body)',
                            textAlign: 'start',
                          }}
                        >
                          <Pencil size={14} />
                          {t('post.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => { copyLink(); setShowMenu(false) }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 14px',
                            border: 'none',
                            borderTop: '1px solid var(--border)',
                            background: 'none',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-sans)',
                            fontSize: 'var(--fs-sm)',
                            color: 'var(--text-body)',
                            textAlign: 'start',
                          }}
                        >
                          <Link2 size={14} />
                          {t('post.copy_link')}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setConfirmDelete(true); setShowMenu(false) }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 14px',
                            border: 'none',
                            borderTop: '1px solid var(--border)',
                            background: 'none',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-sans)',
                            fontSize: 'var(--fs-sm)',
                            color: 'var(--danger)',
                            textAlign: 'start',
                          }}
                        >
                          <Trash2 size={14} />
                          {t('post.delete')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: @username · time */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <button
                type="button"
                onClick={() => navigate(accountPath(post.account))}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-muted)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                @{post.account.username}
              </button>
              <span style={{ color: 'var(--text-faint)', fontSize: 'var(--fs-sm)' }}>·</span>
              <Link
                to={`/posts/${post.id}`}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--text-faint)',
                  flexShrink: 0,
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline' }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none' }}
              >
                {formatPostTime(post.createdAt)}
              </Link>
            </div>
          </div>
        </div>

        {/* Resolve confirm */}
        {confirmResolve && (
          <div
            style={{
              margin: '10px 16px 0',
              padding: '12px 14px',
              background: 'color-mix(in srgb, #16A34A 8%, var(--surface))',
              border: '1px solid color-mix(in srgb, #16A34A 30%, var(--border))',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <p style={{ margin: '0 0 10px', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)' }}>
              {t('help.resolve_confirm')}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setConfirmResolve(false)}
                style={{
                  padding: '7px 14px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--surface)',
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={isResolving}
                onClick={handleMarkResolved}
                style={{
                  padding: '7px 14px',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  background: '#16A34A',
                  color: '#fff',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 600,
                  cursor: isResolving ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: isResolving ? 0.7 : 1,
                }}
              >
                {isResolving && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
                {t('common.confirm')}
              </button>
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {confirmDelete && (
          <div
            style={{
              margin: '10px 16px 0',
              padding: '12px 14px',
              background: 'color-mix(in srgb, var(--danger) 8%, var(--surface))',
              border: '1px solid color-mix(in srgb, var(--danger) 30%, var(--border))',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <p style={{ margin: '0 0 10px', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)' }}>
              {t('post.delete_confirm')}
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                style={{
                  padding: '7px 14px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--surface)',
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={handleDeleteConfirm}
                style={{
                  padding: '7px 14px',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--danger)',
                  color: '#fff',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 600,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: isDeleting ? 0.7 : 1,
                }}
              >
                {isDeleting && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
                {t('post.delete')}
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div
          dir={dir}
          dangerouslySetInnerHTML={{ __html: html }}
          style={{
            padding: '10px 16px 0',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body)',
            color: 'var(--text-body)',
            lineHeight: 'var(--lh-normal)',
            wordBreak: 'break-word',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 4,
          }}
        />

        {/* Category pill (only when set — no "uncategorized" indicator) */}
        {post.category && (
          <div style={{ padding: '8px 16px 0' }}>
            <CategoryChip category={post.category} />
          </div>
        )}

        {/* Action row */}
        {!readOnly && (
          <div style={{ padding: '6px 10px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* Reply */}
            <button
              type="button"
              aria-label="Replies"
              onClick={() => setRepliesOpen((v) => !v)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '7px 9px',
                border: 'none',
                background: repliesOpen ? 'color-mix(in srgb, var(--brand) 10%, transparent)' : 'none',
                cursor: 'pointer',
                borderRadius: 'var(--radius-pill)',
                color: repliesOpen ? 'var(--brand)' : 'var(--text-muted)',
                transition: 'background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out)',
              }}
            >
              <MessageCircle size={16} />
              {(post.replyCount ?? 0) > 0 && (
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', fontWeight: 500 }}>
                  {post.replyCount}
                </span>
              )}
            </button>

            {/* I can help — shown when not own post and not resolved */}
            {!isOwn && !isResolved && (
              <button
                type="button"
                onClick={handleCanHelp}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '6px 12px',
                  border: `1px solid ${accentColor}`,
                  background: 'none',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-pill)',
                  color: accentColor,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 600,
                  transition: 'background var(--dur-fast) var(--ease-out)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `color-mix(in srgb, ${accentColor} 10%, transparent)` }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
              >
                <LifeBuoy size={13} />
                {t('help.can_help')}
              </button>
            )}

            <ShareButton
              postId={post.id}
              authorUsername={post.account.username}
              contentPreview={post.content}
            />

            {/* Report — for non-own posts */}
            {!isOwn && (
              <button
                type="button"
                aria-label="Report"
                onClick={() => {
                  if (!isAuthenticated) { loginRedirect(); return }
                  if (onReport) onReport(post.id)
                  else setShowReportModal(true)
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '7px 9px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-pill)',
                  color: 'var(--text-muted)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
              >
                <Flag size={16} />
              </button>
            )}

            {/* Mark as resolved — own post + not resolved */}
            {isOwn && !isResolved && (
              <button
                type="button"
                aria-label={t('help.mark_resolved')}
                onClick={() => setConfirmResolve(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  marginInlineStart: 'auto',
                  padding: '7px 9px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-pill)',
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-xs)',
                  fontWeight: 500,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)' }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
              >
                <CheckCircle size={15} />
                {t('help.mark_resolved')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Report modal */}
      <ReportModal
        open={showReportModal}
        postId={post.id}
        onClose={() => setShowReportModal(false)}
      />
    </div>
  )
}
